import { withConsoleBase } from '../config';
import { assertCurrentSessionGeneration, notifySessionExpired, sessionGeneration } from '../lib/session-events';

export const attentionReasons = ['delivery_review', 'contact_missing', 'preview_stale', 'reply_recorded'] as const;
export const workspacePopulation = 'Initial prospect outreach: first accepted email and latest recorded reply per prospect; campaign follow-ups excluded';
export const workspaceScope = 'Review cues across stored prospects; replies are not an unread count; eligibility is checked on the record';
export type AttentionReason = typeof attentionReasons[number];
export interface WorkspaceAttention {
  id: number; company_name: string; segment: string; country: string;
  updated_at: string; stage: string; reason: AttentionReason;
}
export interface WorkspaceSnapshot {
  observed_at: string; source: string; population: string;
  window: { start: string; end: string; end_exclusive: true };
  trends: { status: 'complete' | 'unavailable'; reason: string | null;
    daily: Array<{ date: string; accepted: number; replies: number }> };
  attention: { items: WorkspaceAttention[]; counts: Partial<Record<AttentionReason, number>>; scope: string };
}

export function validWorkspace(value: unknown): WorkspaceSnapshot {
  const fail = () => { throw new Error('Workspace evidence could not be verified. Refresh to try again.'); };
  if (!value || typeof value !== 'object') return fail();
  const v = value as WorkspaceSnapshot;
  const text = (x: unknown, max: number) => typeof x === 'string' && x.length <= max;
  const stamp = (x: unknown) => text(x, 40) && /T.*(?:Z|[+-]\d{2}:\d{2})$/.test(x as string) && Number.isFinite(Date.parse(x as string));
  const count = (x: unknown) => Number.isSafeInteger(x) && Number(x) >= 0;
  if (!stamp(v.observed_at) || v.source !== 'prospect_candidates' || v.population !== workspacePopulation
    || !v.window || v.window.end_exclusive !== true || !stamp(v.window.start) || !stamp(v.window.end)
    || Date.parse(v.window.end) - Date.parse(v.window.start) !== 7 * 86400000
    || Date.parse(v.observed_at) < Date.parse(v.window.end)
    || new Date(v.observed_at).toISOString().slice(0, 10) + 'T00:00:00.000Z' !== new Date(v.window.end).toISOString()
    || !v.trends || !['complete', 'unavailable'].includes(v.trends.status)
    || !Array.isArray(v.trends.daily) || !v.attention || !Array.isArray(v.attention.items)
    || v.attention.items.length > 3 || v.attention.scope !== workspaceScope
    || !v.attention.counts || typeof v.attention.counts !== 'object' || Array.isArray(v.attention.counts)) return fail();
  if (v.trends.status === 'complete') {
    if (v.trends.reason !== null || v.trends.daily.length !== 7) return fail();
    v.trends.daily.forEach((day, index) => {
      const expected = new Date(Date.parse(v.window.start) + index * 86400000).toISOString().slice(0, 10);
      if (!day || day.date !== expected || !count(day.accepted) || !count(day.replies)) fail();
    });
  } else if (v.trends.daily.length || v.trends.reason !== 'invalid_milestone_timestamp') return fail();
  if (Object.entries(v.attention.counts).some(([key, value]) => !attentionReasons.includes(key as AttentionReason) || !count(value))) return fail();
  const total = Object.values(v.attention.counts).reduce((sum, value) => sum + (value || 0), 0);
  if (!count(total) || v.attention.items.length !== Math.min(total, 3)) return fail();
  const ids = new Set<number>();
  const reasons: Partial<Record<AttentionReason, number>> = {};
  for (const item of v.attention.items) {
    if (!item || !count(item.id) || !item.id || ids.has(item.id) || !text(item.company_name, 500)
      || !text(item.segment, 500) || !text(item.country, 100) || !stamp(item.updated_at)
      || !['NEW', 'CONTACTED', 'ENGAGED', 'DEMO', 'TRIAL', 'CUSTOMER', 'LOST'].includes(item.stage)
      || !attentionReasons.includes(item.reason) || !v.attention.counts[item.reason]) return fail();
    ids.add(item.id);
    reasons[item.reason] = (reasons[item.reason] || 0) + 1;
    if (reasons[item.reason]! > v.attention.counts[item.reason]!) return fail();
  }
  return v;
}

export async function workspaceSnapshot(signal?: AbortSignal) {
  const generation = sessionGeneration();
  const failed = () => new Error('Workspace evidence is unavailable. Refresh to try again.');
  let response: Response;
  try { response = await fetch(withConsoleBase('/console-api/v1/workspace'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal }); }
  catch { assertCurrentSessionGeneration(generation); throw failed(); }
  assertCurrentSessionGeneration(generation);
  if (!response.ok) {
    if (response.status === 401) notifySessionExpired(generation);
    throw failed();
  }
  let value: unknown;
  try { value = await response.json(); } catch { assertCurrentSessionGeneration(generation); throw failed(); }
  assertCurrentSessionGeneration(generation);
  return validWorkspace(value);
}
